/**
 * Where a failed action becomes something the person can read (parity card P-011).
 *
 * ## The defect this exists for
 * Round 6h's sweep clicked *Mark all as read* on the notifications pane and the browser's
 * `pageerror` fired with `Not authenticated`: the mutation was refused by the api — correctly —
 * and the refusal ended up in the console. Nothing appeared in the UI, so the click looked as
 * if it had worked. That is not a harness artefact: it is the same code path as a session that
 * expired, a permission revoked under the user (F5 emits `room:revoked` for exactly that) and
 * an api redeployed mid-click.
 *
 * ## The shape of the fix
 * Three entry points, one destination:
 *   1. `MutationCache.onError` — every `useMutation` in the app, no matter what its own
 *      `onError` does (the cache callback runs *in addition* to the mutation's, unlike
 *      `defaultOptions.mutations.onError`, which a mutation's own option replaces);
 *   2. `window.onunhandledrejection` — the `void somePromise()` call sites that never went
 *      through TanStack Query (the bell pane's source calls the tRPC client directly);
 *   3. `reportError()` — for a `catch` that wants the standard treatment.
 *
 * The destination is a *sink*, set by `<ErrorToastBridge>` once `UiProvider` is mounted. This
 * module is imported by `createQueryClient()`, which runs before React does, so reports that
 * arrive early are queued rather than lost.
 *
 * Nothing here re-throws, and nothing here swallows a programming error silently: an error with
 * no recognisable shape still reaches the sink, and still reaches the console.
 */

/** What the toast (or any other sink) is told. */
export interface AppErrorReport {
  /** The sentence shown to the person. */
  message: string;
  /** tRPC error code when there is one (`UNAUTHORIZED`, `FORBIDDEN`, `BAD_REQUEST`, …). */
  code?: string | undefined;
  /** Where it came from, for the console line only — never shown. */
  source: 'mutation' | 'rejection' | 'manual';
  /** The original throwable, for the console line only. */
  cause?: unknown;
}

export type ErrorSink = (report: AppErrorReport) => void;

/**
 * The fallbacks. The api's own message is preferred whenever it has one — it is written for
 * the person ("A board name is at most 255 characters"), while the code is written for us.
 */
const CODE_MESSAGES: Record<string, string> = {
  UNAUTHORIZED: 'Your session has ended. Sign in again to continue.',
  FORBIDDEN: 'You do not have permission to do that.',
  NOT_FOUND: 'That item no longer exists. Refresh to see the current state.',
  CONFLICT: 'Somebody changed this first. Refresh and try again.',
  TIMEOUT: 'That took too long. Try again.',
  TOO_MANY_REQUESTS: 'Too many attempts. Wait a moment and try again.',
  PAYLOAD_TOO_LARGE: 'That is too large to upload.',
  INTERNAL_SERVER_ERROR: 'The server could not complete that. Nothing was changed.',
};

/*
 * Deliberately **not** "Something went wrong": that sentence is `PageErrorState`'s route-error
 * copy, and both the sweep (`inpage.pageState().routeError`) and a reader take it to mean the
 * screen itself has crashed. A toast saying it while the screen is perfectly fine reads as a
 * much bigger failure than it is — round 7c's first after-run recorded three `route-crash`
 * entries that were only this toast's wording.
 */
const GENERIC = 'That did not go through. Nothing was changed.';
const OFFLINE = 'The change could not be sent. Check your connection and try again.';

/**
 * A tRPC code without importing server code (the web app never imports `@elizmonday/api` at
 * runtime; it imports its *type* only). `TRPCClientError` carries `data.code`, `shape.data.code`
 * and, for a transport failure, neither.
 */
export function errorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const record = error as { data?: { code?: unknown }; shape?: { data?: { code?: unknown } } };
  const code = record.data?.code ?? record.shape?.data?.code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * P-049 — **the browser refuses to print server internals even if the api sends them.**
 *
 * The defect was a drizzle failure (`Failed query: insert into "sessions" … params: …`) shown
 * on the password-reset page. The fix is the api's — it now answers a generic sentence and an
 * error id — and this is the second, independent guard: any message that still looks like a
 * statement, a parameter list, a stack frame or a path is dropped in favour of the code's own
 * sentence, wherever in the product it arrives.
 */
const SERVER_INTERNALS =
  /failed query|\bparams:|\b(insert\s+into|delete\s+from|alter\s+table)\b|\$\d+\s*,\s*\$\d+|node_modules|\/home\/|\bat\s+\S+\s+\(?[\w./\\-]+\.(?:ts|js|mjs|cjs):\d+:\d+/i;

export const looksLikeServerInternals = (text: string): boolean => SERVER_INTERNALS.test(text);

/** `fetch` failing (offline, api restarting) throws a `TypeError`, with no code and no shape. */
function isTransportFailure(error: unknown): boolean {
  if (errorCode(error) !== undefined) return false;
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /failed to fetch|networkerror|load failed|fetch failed|err_connection/i.test(message);
}

/** The one place that decides what a throwable *says*. Exported because the tests read it. */
export function describeError(error: unknown, source: AppErrorReport['source'] = 'manual'): AppErrorReport {
  const code = errorCode(error);
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const trimmed = raw.trim();
  /*
   * A message is shown only when the api meant it as one. `[\n{]` catches a zod issue list and
   * a stringified JSON body, both of which arrive as the `message` of a `TRPCClientError` and
   * are unreadable in a toast; those fall back to the code's sentence.
   */
  const usable =
    trimmed !== '' &&
    trimmed.length <= 200 &&
    !/[\n{]/.test(trimmed) &&
    // P-049: never, whatever the api said.
    !looksLikeServerInternals(trimmed);
  let message: string;
  if (isTransportFailure(error)) message = OFFLINE;
  else if (code && !usable) message = CODE_MESSAGES[code] ?? GENERIC;
  else if (usable && code === 'UNAUTHORIZED') message = CODE_MESSAGES.UNAUTHORIZED as string;
  else if (usable) message = trimmed;
  else message = GENERIC;
  return { message, code, source, cause: error };
}

/* ------------------------------------------------------------------- the sink */

let sink: ErrorSink | null = null;
/** Reports that arrived before `<ErrorToastBridge>` mounted; replayed when it does. */
const queued: AppErrorReport[] = [];
const QUEUE_LIMIT = 10;

/** Mounted once by `<ErrorToastBridge>`; returns the unsubscribe. */
export function setErrorSink(next: ErrorSink): () => void {
  sink = next;
  while (queued.length > 0) next(queued.shift() as AppErrorReport);
  return () => {
    if (sink === next) sink = null;
  };
}

/**
 * The same refusal arriving five times (a screenful of queries invalidated by one mutation, a
 * realtime reconnect storm) is one message, not five. Short window: a second failure a minute
 * later is news.
 */
const DEDUPE_MS = 4_000;
const lastSeen = new Map<string, number>();

function isRepeat(report: AppErrorReport, now: number): boolean {
  const key = `${report.code ?? ''}::${report.message}`;
  const previous = lastSeen.get(key);
  lastSeen.set(key, now);
  if (lastSeen.size > 50) lastSeen.clear();
  return previous !== undefined && now - previous < DEDUPE_MS;
}

/** Test-only: the dedupe window and the queue are module state. */
export function __resetErrorPlumbing(): void {
  sink = null;
  queued.length = 0;
  lastSeen.clear();
}

/**
 * Report a failure. Never throws, never re-throws, and always leaves a console line: the toast
 * is for the person, the console line is for us.
 */
export function reportError(error: unknown, source: AppErrorReport['source'] = 'manual'): AppErrorReport {
  const report = describeError(error, source);
  const now = Date.now();
  /*
   * `warn`, not `error`. By the time this line runs the failure is *handled*: the person is
   * being shown it. `console.error` is what an unhandled fault looks like, and the sweep's
   * `console-error` check reads it exactly that way — every refused mutation would report a
   * defect. The object is logged in full so the console is still the place to debug from.
   */
  console.warn(`[ez:${report.source}] ${report.message}`, error);
  if (isRepeat(report, now)) return report;
  if (sink) {
    try {
      sink(report);
    } catch (sinkError) {
      // A broken sink must not become the uncaught error this function exists to prevent.
      console.warn('the error sink threw', sinkError);
    }
  } else if (queued.length < QUEUE_LIMIT) {
    queued.push(report);
  }
  return report;
}

/* ------------------------------------------------- the global rejection guard */

export interface GlobalGuardTarget {
  addEventListener(type: string, listener: (event: never) => void): void;
  removeEventListener(type: string, listener: (event: never) => void): void;
}

/**
 * `void somePromise()` is idiomatic in this codebase (182 call sites) and it is *fine* — as
 * long as a rejection lands somewhere. This is that somewhere. `preventDefault()` is what stops
 * Chromium reporting it as an uncaught error, which is the `page-error` the sweep records.
 *
 * Returns the uninstall, which is what the tests use; in the product it lives for the life of
 * the document.
 */
export function installGlobalErrorHandlers(target: Window = window): () => void {
  const onRejection = (event: PromiseRejectionEvent): void => {
    reportError(event.reason, 'rejection');
    event.preventDefault();
  };
  target.addEventListener('unhandledrejection', onRejection);
  return () => target.removeEventListener('unhandledrejection', onRejection);
}
